
import { RevInputAutocomplete } from '@ds/components/InputAutocomplete'
import { RevSkeleton } from '@ds/components/Skeleton'
import algoliasearch from 'algoliasearch/lite'
import debounce from 'debounce'
import isEmpty from 'lodash/isEmpty'

import { BUNDLE_ID, PLATFORM } from '@http/apiConstants'
import APILogger from '@http/apiLogger'

import {
  ALGOLIA_ENDPOINT_NAME,
  AUTOCOMPLETE_DEBOUNCE_DELAY,
  AUTOCOMPLETE_MINIMUM_LENGTH,
} from '../../ModelsComparison.constants'

import translations from './InputModelsAutocomplete.translations'

export default {
  components: {
    RevInputAutocomplete,
    RevSkeleton,
  },

  props: {
    autocompleteConfig: {
      type: Object,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    selectedValues: {
      type: Array,
      default: () => [],
    },
    value: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      options: [],
      selectedValue: this.value || '',
    }
  },

  computed: {
    translations: () => translations,
  },

  watch: {
    $route() {
      // When user clicks the `Back` button of his browser
      // and there's no models anymore we clean the data
      // to empty the inputs.
      const { productA, productB } = this.$route.params
      if (isEmpty(productA) && isEmpty(productB)) {
        this.selectedValue = ''
      }
    },
    value: {
      handler(newValue) {
        if (newValue) {
          this.selectedValue = newValue
        }
      },
    },
  },

  methods: {
    handleInput: debounce(async function debouncedHandleInput(event) {
      const query = event.target.value

      if (query.length >= AUTOCOMPLETE_MINIMUM_LENGTH) {
        // If we already selected models we want different autocomplete options.
        const faceFiltersWithSelectedModels = []
        if (!isEmpty(this.selectedValues)) {
          this.selectedValues.map((value) =>
            faceFiltersWithSelectedModels.push(`model.name:-${value}`),
          )
        }

        const queryConfig = (
          this.autocompleteConfig.queries || [this.autocompleteConfig]
        ).map((config) => ({
          ...config,
          params: {
            ...config.params,
            facetFilters: [
              ...config.params.facetFilters,
              ...faceFiltersWithSelectedModels,
            ],
            query,
          },
        }))

        const apiLogger = new APILogger({
          endpointName: ALGOLIA_ENDPOINT_NAME,
          platform: PLATFORM,
          bundleId: BUNDLE_ID,
          context: queryConfig,
        })

        const message = `GET ${ALGOLIA_ENDPOINT_NAME}`

        apiLogger.attempt({ message })

        try {
          this.options = await this.getModelsOptionsFromAlgolia(queryConfig)

          apiLogger.success({ message })
        } catch (error) {
          // Cancelled: do nothing
          // Errored: must be logged in datadog
          apiLogger.fail({
            message: { message },
            error,
          })
        }
      }
    }, AUTOCOMPLETE_DEBOUNCE_DELAY),

    async getModelsOptionsFromAlgolia(queries) {
      const { ALGOLIA_ID } = this.$config

      const algoliaClient = algoliasearch(
        ALGOLIA_ID,
        this.autocompleteConfig.apiKey,
      )

      // Get the list of matching models
      const {
        results: [{ hits }],
      } = await algoliaClient.multipleQueries(queries)

      // Remove already selected models from the list
      const hitsFiltered = hits.filter(
        (hit) => !this.selectedValues.includes(hit.model.name),
      )

      // Format hits for our `RevInputAutocomplete` options select
      if (!isEmpty(hitsFiltered)) {
        return hitsFiltered.map((hit) => ({
          label: hit.model.name,
          key: hit.objectID,
          value: hit.objectID,
          model: hit.model,
        }))
      }

      return []
    },

    handleSelectModel(...args) {
      const [option, inputId] = args
      this.selectedValue = option.model.name
      this.$emit('model-selected', { model: option.model, inputId })
    },
  },
}
